'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import React, { useMemo } from 'react';

// Premium gradients palette
const GRADIENTS = [
	'linear-gradient(135deg, #FF9A9E 0%, #FECFEF 100%)', // Soft Pink/Peach
	'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', // Lavender to Rose
	'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)', // Mint to Blue
	'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)', // Peach to Purple
	'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)', // Light Purple to Blue
	'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink to Red
	'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue to Cyan
	'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green to Teal
	'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Red to Yellow
	'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Deep Blue to Purple
	'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)', // Light Blue
	'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)', // Purple to Pink
];

export default function CategorySection({
	category,
}: {
	category: {
		id: string;
		name: string;
		slug?: string;
		color?: string;
		icon?: React.ReactNode;
	};
}) {
	// Deterministically pick a gradient based on the category id or name
	const backgroundStyle = useMemo(() => {
		if (category.color) {
			return `linear-gradient(135deg, ${category.color}, ${category.color}dd)`;
		}

		const key = category.id || category.name || 'default';
		let hash = 0;
		for (let i = 0; i < key.length; i++) {
			hash = key.charCodeAt(i) + ((hash << 5) - hash);
		}

		const index = Math.abs(hash) % GRADIENTS.length;
		return GRADIENTS[index];
	}, [category.color, category.id, category.name]);

	return (
		<motion.div
			whileHover={{ y: -6 }}
			transition={{ duration: 0.3, ease: 'easeOut' }}
			className="w-[140px] sm:w-[160px] lg:w-full shrink-0"
		>
			<Link
				href={`/products?category=${encodeURIComponent(
					(category.id && category.id.length === 24) ? category.id : category.name
				)}`}
				className="group flex flex-col h-full"
			>
				<div
					className="relative overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:shadow-xl flex flex-col items-center text-center gap-3 h-full w-full"
					style={{
						background: backgroundStyle,
						border: 'none', // Removed border as gradients provide enough definition
					}}
				>
					{/* Icon Container with Glassmorphism feel */}
					<div
						className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center text-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm backdrop-blur-sm bg-white/20 text-white`}
					>
						{category.icon || <div className="w-8 h-8 rounded-full bg-white/30" />}
					</div>

					{/* Text Content */}
					<div className="space-y-1 relative z-10 flex-1 flex flex-col justify-center w-full">
						<h3 className="font-bold text-sm transition-colors text-white line-clamp-2 leading-tight">
							{category.name}
						</h3>
						<div className="flex items-center justify-center gap-2">
							<span className="h-1 w-0 rounded-full group-hover:w-4 transition-all duration-300 bg-white"></span>
							<p className="text-[10px] font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all text-white/80">
								View All
							</p>
							<span className="h-1 w-0 rounded-full group-hover:w-4 transition-all duration-300 bg-white"></span>
						</div>
					</div>

					{/* Subtle Background Overlay for Depth */}
					<div
						className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl pointer-events-none"
					/>
					<div
						className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-8 -mb-8 blur-2xl pointer-events-none"
					/>
				</div>
			</Link>
		</motion.div>
	);
}
